import React from 'react';
import { Map } from 'mapbox-gl';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { isUiVisibleSelector, whatsNewCountSelector } from 'modules/metadata';
import {
  CompassIcon,
  ZoomMinusIcon,
  ZoomPlusIcon,
  GridIcon,
  TargetIcon,
  ExclusionIcon,
  NewsIcon,
  InclusionIcon,
} from 'modules/ui/assets';
import { MapControlButton, Modal } from 'modules/ui/components';
import { useModal, useToggle } from 'modules/main';
import { Colors } from 'modules/ui/theme/colors';
import { RegularBase } from 'modules/ui/typography';
import { DownloadButtonIndicator } from 'modules/downloads/components';
import {
  useDisplayExclusionZones,
  useGetExclusionZones,
  ExclusionZonesSelectors,
} from 'modules/exclusionZones';
import {
  useDisplayInclusionZones,
  useGetInclusionZones,
  InclusionZonesSelectors,
} from 'modules/inclusionZones';
import WhatsNewModal from '../WhatsNewModal';
import ZonesMenu from '../ZonesMenu';
import {
  useGridVisibility,
  useMapMovementsControls,
  useMeasurementTool,
} from '../../hooks';

import { Container, MapControlsContainer } from './styledComponents';

type Props = {
  map: Map;
};

const MapControl: React.FC<Props> = ({ map }) => {
  const isUiVisible = useSelector(isUiVisibleSelector);
  const whatsNewCount = useSelector(whatsNewCountSelector);
  const exclusionZonesCount = useSelector(
    ExclusionZonesSelectors.enabledZonesCount,
  );
  const inclusionZonesCount = useSelector(
    InclusionZonesSelectors.enabledZonesCount,
  );
  const [isWhatsNewOpened, , openWhatsNewModal, closeWhatsNewModal] =
    useModal(false);
  const { isGridOn, toggleGridVisibility } = useGridVisibility(map);
  const [isZonesSubmenuOpen, toggleIsZonesSubmenuOpen] = useToggle();
  useGetExclusionZones();
  useGetInclusionZones();
  const {
    isExclusionZoneVisibilityOn,
    toggleExclusionZoneVisibility,
    hideExclusionZones,
    showExclusionZones,
  } = useDisplayExclusionZones(map);
  const {
    isInclusionZoneVisibilityOn,
    toggleInclusionZoneVisibility,
    hideInclusionZones,
    showInclusionZones,
  } = useDisplayInclusionZones(map);

  const { handleMessurementToggle, isMeasurementOn } = useMeasurementTool(map);
  const { handleZoomIn, handleZoomOut, handleCenterMap } =
    useMapMovementsControls(map);

  // if (!isUiVisible) return null;

  return (
    <>
      <Modal
        isOpen={isWhatsNewOpened}
        width={720}
        title="What's new"
        closeModal={closeWhatsNewModal}
      >
        <WhatsNewModal />
      </Modal>
      <Container isVisible={isUiVisible}>
        <DownloadButtonIndicator />
        <MapControlsContainer>
          <MapControlButton
            tooltipText="Exclusion zones"
            isActive={isExclusionZoneVisibilityOn}
            onClick={toggleExclusionZoneVisibility}
            indicatorText={
              isExclusionZoneVisibilityOn ? exclusionZonesCount || 0 : 0
            }
          >
            <ExclusionIcon fill={Colors.Destructive} />
          </MapControlButton>
          <MapControlButton
            tooltipText="Inclusion zones"
            isActive={isInclusionZoneVisibilityOn}
            onClick={toggleInclusionZoneVisibility}
            indicatorText={
              isInclusionZoneVisibilityOn ? inclusionZonesCount || 0 : 0
            }
          >
            <InclusionIcon
              fill={
                isInclusionZoneVisibilityOn ? Colors.White : Colors.SolidGray
              }
            />
          </MapControlButton>
          <div style={{ display: 'flex' }}>
            <MapControlButton onClick={toggleIsZonesSubmenuOpen}>
              <RegularBase style={{ paddingBottom: 8 }}>...</RegularBase>
            </MapControlButton>
            <ZonesMenu
              hideExclusionZones={hideExclusionZones}
              hideInclusionZones={hideInclusionZones}
              showExclusionZones={showExclusionZones}
              showInclusionZones={showInclusionZones}
              isExclusionZoneVisibilityOn={isExclusionZoneVisibilityOn}
              isInclusionZoneVisibilityOn={isInclusionZoneVisibilityOn}
              isOpen={isZonesSubmenuOpen}
              map={map}
            />
          </div>
        </MapControlsContainer>
        <MapControlsContainer>
          <MapControlButton tooltipText="Zoom in" onClick={handleZoomIn}>
            <ZoomPlusIcon />
          </MapControlButton>
          <MapControlButton tooltipText="Zoom out" onClick={handleZoomOut}>
            <ZoomMinusIcon />
          </MapControlButton>
          <MapControlButton tooltipText="Locate me" onClick={handleCenterMap}>
            <TargetIcon />
          </MapControlButton>
          <MapControlButton
            tooltipText="Measurement tool"
            onClick={handleMessurementToggle}
            isActive={isMeasurementOn}
          >
            <CompassIcon
              fill={isMeasurementOn ? Colors.White : Colors.SolidGray}
            />
          </MapControlButton>
          <MapControlButton
            tooltipText="Grid lines"
            isActive={isGridOn}
            onClick={toggleGridVisibility}
          >
            <ReactTooltip effect="solid" multiline />
            <GridIcon fill={isGridOn ? Colors.White : Colors.SolidGray} />
          </MapControlButton>
        </MapControlsContainer>
        <MapControlsContainer>
          <MapControlButton
            tooltipText="What’s new"
            indicatorText={whatsNewCount}
            onClick={openWhatsNewModal}
          >
            <NewsIcon />
          </MapControlButton>
        </MapControlsContainer>
      </Container>
    </>
  );
};

export default React.memo(MapControl);
